/**
 * Human-readable labels for activity search facets (TRAV23 activities-front merge).
 *
 * Facet keys arrive from the API in camelCase and are turned into message ids
 * (`activity.duration.<key>` etc.), with the raw key as the `defaultMessage` so an
 * unrecognised facet degrades to the supplier's own wording rather than an empty chip.
 */

import DOMPurify from 'dompurify';
import type { IntlShape } from 'react-intl';

import type { IActivityCategoryCounterItem } from '@common-types';

/** Walk category counter tree and return the display name for a selected key. */
export function findCategoryNameByKey(
  nodes: IActivityCategoryCounterItem[] | undefined,
  key: string,
): string | undefined {
  if (!nodes?.length) return undefined;
  for (const n of nodes) {
    if (String(n.key) === key) return n.name || n.text || undefined;
    const nested = findCategoryNameByKey(n.children, key);
    if (nested) return nested;
  }
  return undefined;
}

export interface ActivityFilterLabelOptions {
  guideLanguageDisplayNames?: Intl.DisplayNames | null;
  categoryNodes?: IActivityCategoryCounterItem[];
}

/** Human-readable label for a single selected activity filter value (chips, empty state, etc.). */
export function formatActivityFilterValueLabel(
  intl: IntlShape,
  filterKey: string,
  value: string | number,
  opts?: ActivityFilterLabelOptions,
): string {
  const v = String(value);
  const guide = opts?.guideLanguageDisplayNames;
  const cats = opts?.categoryNodes;

  switch (filterKey) {
    case 'timeOfDays':
      return activityTimeOfDayLabel(intl, v);
    case 'specials':
      return activitySpecialLabel(intl, v);
    case 'durations':
      return activityDurationLabel(intl, v);
    case 'guideLanguages': {
      const localized = guide?.of(v);
      return localized ?? v.toUpperCase();
    }
    case 'refundable':
      return activityRefundableLabel(intl, v);
    case 'activityCategory':
      return findCategoryNameByKey(cats, v) ?? v;
    default:
      return v;
  }
}

/** Labels for activity search counter keys (API camelCase). */
export function activityDurationLabel(intl: IntlShape, key: string): string {
    return intl.formatMessage({
        id: `activity.duration.${key}`,
        defaultMessage: key,
    });
}

export function activityTimeOfDayLabel(intl: IntlShape, key: string): string {
    return intl.formatMessage({
        id: `activity.timeOfDay.${key}`,
        defaultMessage: key,
    });
}

export function activitySpecialLabel(intl: IntlShape, key: string): string {
    return intl.formatMessage({
        id: `activity.special.${key}`,
        defaultMessage: key,
    });
}

export function activityRefundableLabel(intl: IntlShape, key: string): string {
    const id =
        key === 'Non Refundable'
            ? 'activity.refundable.nonRefundable'
            : 'activity.refundable.refundable';
    return intl.formatMessage({ id, defaultMessage: key });
}

/**
 * Sanitises supplier-provided HTML and strips the empty tags they commonly emit
 * (TRAV23 activities-front merge). Sanitising is not optional here: activity
 * descriptions are rendered with dangerouslySetInnerHTML.
 */
export const cleanHtml = (html?: string): string => {
  if (!html) return "";

  // First sanitize (security)
  const sanitized = DOMPurify.sanitize(html);

  // Remove empty tags like <p></p>, <p><br></p>, <div>&nbsp;</div>
  return sanitized.replace(
    /<([a-z][a-z0-9]*)[^>]*>(\s|&nbsp;|<br\s*\/?>)*<\/\1>/gi,
    ""
  );
};
